<template>
    <div class="faq-question">
        <div
            class="hidden-anchor"
            :id="`question-anchor-${questionAnswerIn.id}`"></div>
        <div
            class="faq-question-item"
            role="tablist">
            <div
                class="faq-question-header"
                role="tab">
                <b-button
                    class="btn-faq-answer-toggle"
                    block
                    variant="outline-primary"
                    @click="collapse()"
                    :class="collapsed && 'active'">
                    <div class="faq-question-title">
                        <div class="title-wrapper">
                            <i
                                class="fas fa-fw fa-user-lock teacher-icon"
                                v-b-tooltip.hover
                                v-if="questionAnswerIn.teacher"
                                :title="$t('admin.visible_to_teacher')"></i>
                            <i
                                class="fas fa-fw fa-user-graduate student-icon"
                                v-b-tooltip.hover
                                v-if="questionAnswerIn.student"
                                :title="$t('admin.visible_to_student')"></i>
                            {{ questionAnswerIn.question }}
                        </div>
                        <div
                            class="labels"
                            v-if="afaqTags.length > 0">
                            <b-badge
                                class="faq-question-label"
                                v-for="tag in afaqTags"
                                :style="getStyle(tag)"
                                pill
                                variant="info"
                                :key="tag.id">
                                {{ tag.name }}
                            </b-badge>
                        </div>
                    </div>

                    <i class="fas fa-fw fa-chevron-down state-icon"></i>

                    <div class="context-nav context-question-nav">
                        <b-dropdown
                            class="ellipsis-nav bordered-menu faq-question-nav"
                            no-caret
                            variant="primary"
                            right
                            toggle-class="question-context-menu">
                            <template v-slot:button-content>
                                <i class="fas fa-ellipsis-v"></i>
                            </template>

                            <b-dropdown-item
                                class="faq-question-edit"
                                @click.prevent.stop="editQA()">
                                <i class="btn-edit"></i>{{ $t('admin.faq_edit_faq') }}
                            </b-dropdown-item>

                            <b-dropdown-item
                                class="faq-question-remove"
                                @click.prevent.stop="removeQA()">
                                <i class="btn-remove"></i>{{ $t('admin.faq_delete_faq') }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </b-button>

                <b-collapse
                    class="faq-answer-content"
                    v-model="collapsed"
                    :id="'collapse-answer-' + questionAnswerIn.id"
                    role="tabpanel">
                    <design-2021-apprentice-display-html-component
                        class="answer-text"
                        :html-content="questionAnswerIn.answer">
                    </design-2021-apprentice-display-html-component>
                </b-collapse>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-admin-afaq-question-component',
    props: ['questionAnswerIn', 'afaqTagsIn'],
    data() {
        return {
            collapsed: true,
        };
    },
    computed: {
        afaqTags() {
            if (!this.questionAnswerIn.afaq_tags) {
                return [];
            }

            return this.questionAnswerIn.afaq_tags;
        },
    },
    methods: {
        collapse() {
            this.collapsed = !this.collapsed;
        },

        editQA() {
            this.$emit('editQuestion', this.questionAnswerIn);
        },

        removeQA() {
            this.alert
                .confirm(this.$t('admin.faq_delete_faq_title'), this.$t('admin.faq_delete_faq_confirm'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios
                        .delete(this.routes.afaqCategoryQuestion(this.questionAnswerIn.afaq_category_id), {
                            data: { questionId: this.questionAnswerIn.id },
                        })
                        .then(
                            (response) => {
                                this.questionAnswerIn.remove();
                                this.$emit['question-answer-removed'];
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                });
        },

        getStyle(label) {
            return {
                backgroundColor: label.color,
            };
        },
    },
};
</script>

<style scoped lang="scss">
.faq-question {
    &:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.btn-faq-answer-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border: 1px solid var(--primary);
    outline: none;
    box-shadow: none;
    border-radius: $border-radius-content;
    line-height: 1;

    .faq-question-title {
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        margin-right: map-get($grid-gutter-spacing, 'fifteen');

        .title-wrapper {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            flex-wrap: wrap;
            column-gap: map-get($grid-gutter-spacing, 'fifteen');
        }

        .labels {
            flex-wrap: wrap;
            align-items: center;
            column-gap: map-get($grid-gutter-spacing, 'fifteen');
            margin-top: map-get($grid-gutter-spacing, 'four');
        }

        &,
        .labels {
            display: flex;
            justify-content: flex-start;
        }
    }

    .faq-question-label {
        padding: map-get($grid-gutter-spacing, 'four') map-get($grid-gutter-spacing, 'eight');
    }

    .state-icon {
        transform-origin: center;
        transition: all 0.8s ease;
        margin-left: auto;
    }

    &:hover {
        background-color: $info;
        font-weight: $font-weight-bold;

        &,
        .sub-assignment-drag-handle {
            color: $white;
        }
    }

    &.active,
    &:not(:disabled):not(.disabled).active {
        background-color: $info;
        font-weight: $font-weight-bold;
        border: 1px solid $info;

        .state-icon {
            transform: rotate(-180deg);
        }
    }
}

.faq-answer-content {
    margin: map-get($grid-gutter-spacing, 'twenty-one') 0;
    padding: 0 $grid-gutter-width;
}
</style>
